import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  Update as UpdateIcon,
  CheckCircle as CheckCircleIcon,
  PauseCircle as PauseCircleIcon,
  PlayCircle as PlayCircleIcon,
  Search as SearchIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface LinkTarget {
  url: string;
  matchType: 'contains' | 'exact';
}

interface Campaign {
  id: number;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: 'active' | 'inactive' | 'completed';
  influencer_count: number;
  tracked_story_count: number;
  tracked_post_count?: number;
  content_tracking_mode?: 'story' | 'feed' | 'both';
  feed_post_limit?: number;
  target_hashtags?: string[] | string;
  target_mentions?: string[] | string;
  target_links?: LinkTarget[] | string[] | string;
  target_caption_keywords?: string[];
  created_at: string;
  auto_check_enabled: boolean;
  auto_check_interval: number;
  auto_check_start_time?: string | null;
  auto_check_end_time?: string | null;
  check_only_undetected?: boolean;
  last_auto_check: string | null;
  updated_at: string;
}

function getSharingTypeLabel(mode?: 'story' | 'feed' | 'both'): string {
  switch (mode) {
    case 'feed':
      return 'Gönderi';
    case 'both':
      return 'Hikaye + Gönderi';
    case 'story':
    default:
      return 'Hikaye';
  }
}

const padTimeComponent = (value: string) => value.padStart(2, '0');

const extractHourMinute = (value: string | null | undefined) => {
  if (!value) {
    return { hours: '00', minutes: '00' };
  }
  const parts = `${value}`.split(':');
  const hours = padTimeComponent(parts[0] || '00');
  const minutes = padTimeComponent(parts[1] || '00');
  return { hours, minutes };
};

const formatTimeForInput = (value: string | null | undefined) => {
  const { hours, minutes } = extractHourMinute(value);
  return `${hours}:${minutes}`;
};

const normalizeTimeForApi = (value: string | null | undefined) => {
  const { hours, minutes } = extractHourMinute(value);
  const seconds = '00';
  return `${hours}:${minutes}:${seconds}`;
};

const formatTimeRangeDisplay = (start: string | null | undefined, end: string | null | undefined) => {
  const startFormatted = formatTimeForInput(start ?? '00:00');
  const endFormatted = formatTimeForInput(end ?? '23:59');
  return `${startFormatted} - ${endFormatted}`;
};

const parseUtcDateString = (value: string | null | undefined) => {
  if (!value) return null;
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = hasTimezone ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseStringArrayField = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s || '').trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw || '[]');
      return Array.isArray(p) ? p.map((s) => String(s || '').trim()).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeLinkTargets = (raw: unknown): LinkTarget[] => {
  if (!raw) return [];
  let arr: unknown[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw || '[]');
      arr = Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return arr.map((item) => {
    if (typeof item === 'string') {
      return { url: item.trim(), matchType: 'contains' as const };
    }
    const o = item as { url?: string; matchType?: string };
    return {
      url: String(o?.url || '').trim(),
      matchType: (o?.matchType === 'exact' ? 'exact' : 'contains') as 'contains' | 'exact'
    };
  }).filter((x) => x.url);
};

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'active' as 'active' | 'inactive' | 'completed',
    auto_check_enabled: false,
    auto_check_interval: 360,
    auto_check_start_time: '00:00',
    auto_check_end_time: '23:59',
    check_only_undetected: false,
    content_tracking_mode: 'story' as 'story' | 'feed' | 'both',
    feed_post_limit: 12,
    target_hashtags: [] as string[],
    target_mentions: [] as string[],
    target_links: [] as LinkTarget[],
    target_caption_keywords: [] as string[]
  });
  const [intervalValue, setIntervalValue] = useState(6);
  const [intervalUnit, setIntervalUnit] = useState<'minutes' | 'hours'>('hours');
  const [newCaptionKeyword, setNewCaptionKeyword] = useState('');
  const [newHashtag, setNewHashtag] = useState('');
  const [newMention, setNewMention] = useState('');
  const [newLink, setNewLink] = useState('');
  const [newLinkMatchType, setNewLinkMatchType] = useState<'contains' | 'exact'>('contains');
  const navigate = useNavigate();

  // Yeni state'ler
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'completed'>('all');
  const [stats, setStats] = useState({
    total_campaigns: 0,
    active_campaigns: 0,
    inactive_campaigns: 0,
    completed_campaigns: 0
  });

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Kampanyaları çek ve durumlarına göre say
      const response = await axios.get('/api/campaigns');
      const campaigns = response.data.campaigns;
      
      const stats = {
        total_campaigns: campaigns.length,
        active_campaigns: campaigns.filter((c: Campaign) => c.status === 'active').length,
        inactive_campaigns: campaigns.filter((c: Campaign) => c.status === 'inactive').length,
        completed_campaigns: campaigns.filter((c: Campaign) => c.status === 'completed').length
      };
      
      setStats(stats);
    } catch (error) {
      console.error('İstatistik yükleme hatası:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get('/api/campaigns');
      setCampaigns(response.data.campaigns);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kampanyalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Filtreleme ve sıralama (useMemo ile optimize edilmiş)
  const filteredAndSortedCampaigns = useMemo(() => {
    return campaigns
      .filter(campaign => {
        const titleMatch = (campaign.title || '').toLowerCase().includes(searchTerm.toLowerCase());
        const descriptionMatch = (campaign.description || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesSearch = titleMatch || descriptionMatch;
        const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        // Önce aktif kampanyalar, sonra diğerleri
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        
        // Aynı durumda ise tarihe göre sırala (en yeni önce)
        const aDate = new Date(a.created_at).getTime();
        const bDate = new Date(b.created_at).getTime();
        return bDate - aDate;
      });
  }, [campaigns, searchTerm, statusFilter]);

  const handleOpenDialog = (campaign?: Campaign) => {
    if (campaign) {
      setEditingCampaign(campaign);
      const minutes = (campaign as any).auto_check_interval || 360;
      
      // Dakikayı saat/dakikaya çevir
      if (minutes >= 60 && minutes % 60 === 0) {
        setIntervalValue(minutes / 60);
        setIntervalUnit('hours');
      } else {
        setIntervalValue(minutes);
        setIntervalUnit('minutes');
      }
      
      const capRaw = (campaign as any).target_caption_keywords;
      let capKw: string[] = [];
      try {
        if (Array.isArray(capRaw)) capKw = capRaw.map((s: string) => String(s || '').trim()).filter(Boolean);
        else if (typeof capRaw === 'string') capKw = JSON.parse(capRaw || '[]');
      } catch {
        capKw = [];
      }
      const fl = Number((campaign as any).feed_post_limit);
      const feed_post_limit = Math.min(50, Math.max(1, fl > 0 ? fl : 12));

      setFormData({
        title: campaign.title,
        description: campaign.description,
        start_date: campaign.start_date || '',
        end_date: campaign.end_date || '',
        status: campaign.status as 'active' | 'inactive' | 'completed',
        auto_check_enabled: (campaign as any).auto_check_enabled || false,
        auto_check_interval: (campaign as any).auto_check_interval || 360,
        auto_check_start_time: formatTimeForInput((campaign as any).auto_check_start_time),
        auto_check_end_time: formatTimeForInput((campaign as any).auto_check_end_time),
        check_only_undetected: campaign.check_only_undetected || false,
        content_tracking_mode: (campaign as any).content_tracking_mode || 'story',
        feed_post_limit,
        target_hashtags: parseStringArrayField((campaign as any).target_hashtags),
        target_mentions: parseStringArrayField((campaign as any).target_mentions),
        target_links: normalizeLinkTargets((campaign as any).target_links),
        target_caption_keywords: capKw
      });
    } else {
      setEditingCampaign(null);
      setIntervalValue(6);
      setIntervalUnit('hours');
      setFormData({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'active' as 'active' | 'inactive' | 'completed',
        auto_check_enabled: false,
        auto_check_interval: 360,
        auto_check_start_time: '00:00',
        auto_check_end_time: '23:59',
        check_only_undetected: false,
        content_tracking_mode: 'story',
        feed_post_limit: 12,
        target_hashtags: [],
        target_mentions: [],
        target_links: [],
        target_caption_keywords: []
      });
    }
    setNewCaptionKeyword('');
    setNewHashtag('');
    setNewMention('');
    setNewLink('');
    setNewLinkMatchType('contains');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCampaign(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Interval'i dakikaya çevir
      const intervalInMinutes = intervalUnit === 'hours' 
        ? intervalValue * 60 
        : intervalValue;
      
      const dataToSend = {
        ...formData,
        auto_check_interval: intervalInMinutes,
        auto_check_start_time: normalizeTimeForApi(formData.auto_check_start_time),
        auto_check_end_time: normalizeTimeForApi(formData.auto_check_end_time),
        check_only_undetected: formData.check_only_undetected,
        content_tracking_mode: formData.content_tracking_mode,
        feed_post_limit: Math.min(50, Math.max(1, Number(formData.feed_post_limit) || 12)),
        target_hashtags: formData.target_hashtags,
        target_mentions: formData.target_mentions,
        target_links: formData.target_links,
        target_caption_keywords:
          formData.content_tracking_mode === 'story' ? [] : formData.target_caption_keywords
      };
      
      if (editingCampaign) {
        await axios.put(`/api/campaigns/${editingCampaign.id}`, dataToSend);
      } else {
        await axios.post('/api/campaigns', dataToSend);
      }
      handleCloseDialog();
      fetchCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Bu kampanyayı silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`/api/campaigns/${id}`);
        fetchCampaigns();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Silme işlemi başarısız');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'inactive': return 'Pasif';
      case 'completed': return 'Tamamlandı';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Kampanyalar
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Yeni Kampanya
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* İstatistik Kartları */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <AnalyticsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" color="primary">
              {stats.total_campaigns}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Kampanya
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" color="success.main">
              {stats.active_campaigns}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aktif Kampanya
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <PauseCircleIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" color="warning.main">
              {stats.inactive_campaigns}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pasif Kampanya
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <PlayCircleIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" color="info.main">
              {stats.completed_campaigns}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tamamlanan Kampanya
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Arama */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Kampanya ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  label="Durum"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="inactive">Pasif</MenuItem>
                  <MenuItem value="completed">Tamamlandı</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {filteredAndSortedCampaigns.map((campaign) => {
          const lastAutoCheckDate = parseUtcDateString(campaign.last_auto_check);

          return (
          <Grid item xs={12} md={6} lg={4} key={campaign.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="h2">
                    {campaign.title}
                  </Typography>
                  <Chip
                    label={getStatusText(campaign.status)}
                    color={getStatusColor(campaign.status) as any}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {campaign.description}
                </Typography>

                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">
                    <strong>Başlangıç:</strong> {new Date(campaign.start_date).toLocaleDateString('tr-TR')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Bitiş:</strong> {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString('tr-TR') : 'Belirsiz'}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">
                    <strong>Influencer:</strong> {campaign.influencer_count}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tespit Edilen:</strong> {campaign.tracked_story_count}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Paylaşım Türü:</strong> {getSharingTypeLabel(campaign.content_tracking_mode)}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                {/* Otomatik Takip ve Son Güncelleme Bilgileri */}
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <ScheduleIcon fontSize="small" color={campaign.auto_check_enabled ? 'success' : 'disabled'} />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Otomatik Takip: {campaign.auto_check_enabled ? (
                        <Chip 
                          label={`Aktif (${campaign.auto_check_interval >= 60 ? `${campaign.auto_check_interval / 60}sa` : `${campaign.auto_check_interval}dk`})`} 
                          size="small" 
                          color="success" 
                          sx={{ ml: 0.5, height: 20 }}
                        />
                      ) : (
                        <Chip label="Kapalı" size="small" color="default" sx={{ ml: 0.5, height: 20 }} />
                      )}
                    </Typography>
                  </Box>
                  {campaign.auto_check_enabled && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3, mb: 0.5 }}>
                      Çalışma aralığı: {formatTimeRangeDisplay(campaign.auto_check_start_time, campaign.auto_check_end_time)}
                    </Typography>
                  )}
                  <Box display="flex" alignItems="center" gap={1}>
                    <UpdateIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Son Güncelleme: {new Date(campaign.updated_at).toLocaleString('tr-TR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                  {lastAutoCheckDate && (
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                        Son Otomatik Kontrol: {lastAutoCheckDate.toLocaleString('tr-TR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  >
                    <ViewIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(campaign)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(campaign.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kampanya Başlığı"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Açıklama"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Başlangıç Tarihi"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Bitiş Tarihi (İsteğe Bağlı)"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  helperText="Boş bırakılırsa süresiz olur"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Durum</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' | 'completed' })}
                    label="Durum"
                  >
                    <MenuItem value="active">Aktif</MenuItem>
                    <MenuItem value="inactive">Pasif</MenuItem>
                    <MenuItem value="completed">Tamamlandı</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>İçerik takip türü</InputLabel>
                  <Select
                    value={formData.content_tracking_mode}
                    onChange={(e) => {
                      const next = e.target.value as 'story' | 'feed' | 'both';
                      setFormData({
                        ...formData,
                        content_tracking_mode: next,
                        ...(next === 'story' ? { target_caption_keywords: [] } : {})
                      });
                    }}
                    label="İçerik takip türü"
                  >
                    <MenuItem value="story">Hikâye</MenuItem>
                    <MenuItem value="feed">Gönderi ve Reels</MenuItem>
                    <MenuItem value="both">İkisi</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {(formData.content_tracking_mode === 'story' || formData.content_tracking_mode === 'both') && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Hikâye takip kriterleri
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Hikâye taramasında kullanılacak hashtag, mention ve linkler
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Hashtag&apos;ler
                    </Typography>
                    <Box display="flex" gap={1} mb={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Yeni hashtag"
                        value={newHashtag}
                        onChange={(e) => setNewHashtag(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const t = newHashtag.trim().replace(/^#/, '');
                            if (!t) return;
                            setFormData({
                              ...formData,
                              target_hashtags: [...formData.target_hashtags, t]
                            });
                            setNewHashtag('');
                          }
                        }}
                        placeholder="örnek"
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const t = newHashtag.trim().replace(/^#/, '');
                          if (!t) return;
                          setFormData({
                            ...formData,
                            target_hashtags: [...formData.target_hashtags, t]
                          });
                          setNewHashtag('');
                        }}
                      >
                        Ekle
                      </Button>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                      {formData.target_hashtags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={`#${tag}`}
                          onDelete={() =>
                            setFormData({
                              ...formData,
                              target_hashtags: formData.target_hashtags.filter((_, i) => i !== index)
                            })
                          }
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Mention&apos;lar
                    </Typography>
                    <Box display="flex" gap={1} mb={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Yeni mention"
                        value={newMention}
                        onChange={(e) => setNewMention(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const t = newMention.trim().replace(/^@/, '');
                            if (!t) return;
                            setFormData({
                              ...formData,
                              target_mentions: [...formData.target_mentions, t]
                            });
                            setNewMention('');
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const t = newMention.trim().replace(/^@/, '');
                          if (!t) return;
                          setFormData({
                            ...formData,
                            target_mentions: [...formData.target_mentions, t]
                          });
                          setNewMention('');
                        }}
                      >
                        Ekle
                      </Button>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                      {formData.target_mentions.map((mention, index) => (
                        <Chip
                          key={index}
                          label={`@${mention}`}
                          onDelete={() =>
                            setFormData({
                              ...formData,
                              target_mentions: formData.target_mentions.filter((_, i) => i !== index)
                            })
                          }
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Link&apos;ler
                    </Typography>
                    <Grid container spacing={1} mb={1}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="URL"
                          value={newLink}
                          onChange={(e) => setNewLink(e.target.value)}
                          placeholder="https://..."
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Eşleşme</InputLabel>
                          <Select
                            value={newLinkMatchType}
                            label="Eşleşme"
                            onChange={(e) => setNewLinkMatchType(e.target.value as 'contains' | 'exact')}
                          >
                            <MenuItem value="contains">İçeren</MenuItem>
                            <MenuItem value="exact">Tam</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => {
                            const u = newLink.trim();
                            if (!u) return;
                            setFormData({
                              ...formData,
                              target_links: [...formData.target_links, { url: u, matchType: newLinkMatchType }]
                            });
                            setNewLink('');
                          }}
                        >
                          Ekle
                        </Button>
                      </Grid>
                    </Grid>
                    <Box display="flex" flexDirection="column" gap={1} mb={2}>
                      {formData.target_links.map((linkObj, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={`${linkObj.url} (${linkObj.matchType === 'exact' ? 'Tam' : 'İçeren'})`}
                            onDelete={() =>
                              setFormData({
                                ...formData,
                                target_links: formData.target_links.filter((_, i) => i !== index)
                              })
                            }
                          />
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </>
              )}

              {(formData.content_tracking_mode === 'feed' || formData.content_tracking_mode === 'both') && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Son kaç gönderiye bakılsın"
                    value={formData.feed_post_limit}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setFormData({
                        ...formData,
                        feed_post_limit: Number.isNaN(v) ? 12 : Math.min(50, Math.max(1, v))
                      });
                    }}
                    inputProps={{ min: 1, max: 50 }}
                    helperText="1–50 (gönderi / Reels taraması)"
                  />
                </Grid>
              )}

              {(formData.content_tracking_mode === 'feed' ||
                formData.content_tracking_mode === 'both') && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Caption anahtar kelimeleri
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Gönderi / Reels açıklama metninde aranacak ifadeler
                  </Typography>
                  <Box display="flex" gap={1} mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Yeni ifade"
                      value={newCaptionKeyword}
                      onChange={(e) => setNewCaptionKeyword(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const t = newCaptionKeyword.trim();
                          if (!t) return;
                          setFormData({
                            ...formData,
                            target_caption_keywords: [...formData.target_caption_keywords, t]
                          });
                          setNewCaptionKeyword('');
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const t = newCaptionKeyword.trim();
                        if (!t) return;
                        setFormData({
                          ...formData,
                          target_caption_keywords: [...formData.target_caption_keywords, t]
                        });
                        setNewCaptionKeyword('');
                      }}
                    >
                      Ekle
                    </Button>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {formData.target_caption_keywords.map((phrase, index) => (
                      <Chip
                        key={index}
                        label={phrase}
                        onDelete={() =>
                          setFormData({
                            ...formData,
                            target_caption_keywords: formData.target_caption_keywords.filter((_, i) => i !== index)
                          })
                        }
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Otomatik Kontrol Ayarları */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.05)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    🤖 Otomatik Hikaye Kontrolü
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Kampanya oluşturduktan sonra influencer ekleyin ve otomatik kontrol başlayacak
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.auto_check_enabled}
                        onChange={(e) => setFormData({...formData, auto_check_enabled: e.target.checked})}
                        color="primary"
                      />
                    }
                    label="Otomatik kontrol aktif"
                  />
                  
                  {formData.auto_check_enabled && (
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Kontrol Aralığı"
                            value={intervalValue}
                            onChange={(e) => setIntervalValue(parseInt(e.target.value) || 1)}
                            inputProps={{ min: 1, max: intervalUnit === 'hours' ? 24 : 1440 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth>
                            <InputLabel>Birim</InputLabel>
                            <Select
                              value={intervalUnit}
                              onChange={(e) => setIntervalUnit(e.target.value as 'minutes' | 'hours')}
                              label="Birim"
                            >
                              <MenuItem value="minutes">Dakika</MenuItem>
                              <MenuItem value="hours">Saat</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="time"
                            label="Başlangıç Saati"
                            value={formData.auto_check_start_time}
                            onChange={(e) => setFormData({ ...formData, auto_check_start_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="time"
                            label="Bitiş Saati"
                            value={formData.auto_check_end_time}
                            onChange={(e) => setFormData({ ...formData, auto_check_end_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                          />
                        </Grid>
                      </Grid>
                      
                      {/* Hazır Ayarlar */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                          Hızlı Ayarlar:
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          <Chip 
                            label="30 dk" 
                            size="small" 
                            clickable
                            onClick={() => { setIntervalValue(30); setIntervalUnit('minutes'); }}
                            color={intervalValue === 30 && intervalUnit === 'minutes' ? 'primary' : 'default'}
                          />
                          <Chip 
                            label="1 saat" 
                            size="small" 
                            clickable
                            onClick={() => { setIntervalValue(1); setIntervalUnit('hours'); }}
                            color={intervalValue === 1 && intervalUnit === 'hours' ? 'primary' : 'default'}
                          />
                          <Chip 
                            label="2 saat" 
                            size="small" 
                            clickable
                            onClick={() => { setIntervalValue(2); setIntervalUnit('hours'); }}
                            color={intervalValue === 2 && intervalUnit === 'hours' ? 'primary' : 'default'}
                          />
                          <Chip 
                            label="4 saat" 
                            size="small" 
                            clickable
                            onClick={() => { setIntervalValue(4); setIntervalUnit('hours'); }}
                            color={intervalValue === 4 && intervalUnit === 'hours' ? 'primary' : 'default'}
                          />
                          <Chip 
                            label="6 saat" 
                            size="small" 
                            clickable
                            onClick={() => { setIntervalValue(6); setIntervalUnit('hours'); }}
                            color={intervalValue === 6 && intervalUnit === 'hours' ? 'primary' : 'default'}
                          />
                          <Chip 
                            label="12 saat" 
                            size="small" 
                            clickable
                            onClick={() => { setIntervalValue(12); setIntervalUnit('hours'); }}
                            color={intervalValue === 12 && intervalUnit === 'hours' ? 'primary' : 'default'}
                          />
                          <Chip 
                            label="24 saat" 
                            size="small" 
                            clickable
                            onClick={() => { setIntervalValue(24); setIntervalUnit('hours'); }}
                            color={intervalValue === 24 && intervalUnit === 'hours' ? 'primary' : 'default'}
                          />
                        </Box>
                      </Box>
                      
                      <Typography variant="caption" color="primary" display="block" sx={{ mt: 2, fontWeight: 'bold' }}>
                        ⏰ {intervalUnit === 'hours' ? `${intervalValue} saat = ${intervalValue * 60} dakika` : `${intervalValue} dakika`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Şu anki saat: {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })} · Çalışma aralığı: {formatTimeRangeDisplay(formData.auto_check_start_time, formData.auto_check_end_time)}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.check_only_undetected || false}
                            onChange={(e) => setFormData({...formData, check_only_undetected: e.target.checked})}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              Sadece tespit edilemeyenleri kontrol et
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Aktif olduğunda, sadece henüz hiç hikaye tespit edilmemiş (0 paylaşım) kullanıcılar kontrol edilir. API kullanımını azaltır.
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>İptal</Button>
            <Button type="submit" variant="contained">
              {editingCampaign ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Campaigns;
